import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PostCard from "./PostCard";
import { getBaseUrl } from "../../api/api";
import { vi as viLocale, enUS as enUSLocale } from "date-fns/locale";
import { localDateTimeInTimeZone } from "../helpers";
import { format } from "date-fns";

const scrollbarStyles = `
  .posts-list-scrollable::-webkit-scrollbar {
    width: 4px;
  }
  .posts-list-scrollable::-webkit-scrollbar-track {
    background: transparent;
  }
  .posts-list-scrollable::-webkit-scrollbar-thumb {
    background: #D1D5DB;
    border-radius: 2px;
  }
  .posts-list-scrollable::-webkit-scrollbar-thumb:hover {
    background: #9CA3AF;
  }
  .posts-list-scrollable {
    scrollbar-width: thin;
    scrollbar-color: #D1D5DB transparent;
  }
`;

interface PostsListProps {
  socialPosts: any[];
  isGenerating: boolean;
  onPostClick: (postData: any) => void;
  calendarViewHeight: number;
  onDeletePost?: (postId: string) => void;
}

const PostsList: React.FC<PostsListProps> = ({
  socialPosts,
  isGenerating,
  onPostClick,
  calendarViewHeight,
  onDeletePost,
}) => {
  const { t, i18n } = useTranslation();
  const platformFilter = useSelector(
    (state: any) => state.aiContent?.get?.("platformFilter") || "All"
  );
  const statusFilter = useSelector((state: any) => state.aiContent?.get?.("statusFilter") || "All");
  const timezoneFilter = useSelector(
    (state: any) => state.aiContent?.get?.("timezoneFilter") || "All"
  );

  const locale = i18n.language?.startsWith("vi") ? viLocale : enUSLocale;

  const getPostTimezone = (post: any): string => {
    const postTimezone = post?.data?.timezone?.value;
    if (postTimezone) {
      return postTimezone;
    }
    if (timezoneFilter && timezoneFilter !== "All") {
      return String(timezoneFilter);
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  const getPostDateInfo = (post: any) => {
    if (!post) return null;
    const timezone = getPostTimezone(post);
    const rawValue =
      post.data?.scheduledTime?.value ??
      post.data?.start?.value ??
      post.data?.scheduledTimeUTC ??
      post.data?.startTime?.value;

    if (!rawValue) {
      return null;
    }

    const baseDate = new Date(rawValue);
    if (isNaN(baseDate.getTime())) {
      return null;
    }

    const startDate = localDateTimeInTimeZone(baseDate, timezone);

    try {
      const isoKey = format(startDate, "yyyy-MM-dd", {
        locale,
      });
      const displayDate = format(startDate, "EEEE, MMMM dd, yyyy", { locale });
      return {
        timezone,
        isoKey,
        displayDate,
        epoch: startDate.getTime(),
      };
    } catch {
      return null;
    }
  };

  return (
    <aside
      className={`w-full lg:w-[287px] bg-white flex flex-col ${calendarViewHeight ? "" : "h-full"}`}
      style={calendarViewHeight ? { height: `${calendarViewHeight}px` } : {}}
    >
      <style>{scrollbarStyles}</style>
      <div className="p-4 lg:p-0 lg:mb-2.5 lg:hidden">
        <div className="flex items-center gap-1">
          <span className="text-base font-bold text-gray-900">{t("Posts")}</span>
        </div>
      </div>

      <div className="relative flex-1 overflow-auto px-4 lg:px-0 posts-list-scrollable">
        <div className="space-y-4 lg:space-y-8">
          {isGenerating ? (
            Array.from({ length: 3 }).map((_, gi) => (
              <div key={`skeleton-group-${gi}`}>
                <div className="text-xs text-gray-400 mb-2">
                  <div className="h-3 w-32 sm:w-44 bg-gray-100 rounded animate-pulse" />
                </div>
                {Array.from({ length: 3 }).map((_, ci) => (
                  <div
                    key={`skeleton-card-${gi}-${ci}`}
                    className="mb-1 p-2 sm:p-3 rounded-[12px] border border-gray-100 bg-gray-50"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="inline-flex items-center justify-center rounded-full w-6 h-6 sm:w-[26px] sm:h-[26px] bg-gray-200 animate-pulse" />
                      </div>
                      <span className="h-4 w-12 sm:w-16 bg-gray-200 rounded animate-pulse" />
                    </div>
                    <div className="mt-2 space-y-2">
                      <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                      <div className="h-3 w-4/5 sm:w-5/6 bg-gray-200 rounded animate-pulse" />
                    </div>
                    <div className="mt-2 h-5 w-20 sm:w-24 bg-gray-200 rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ))
          ) : socialPosts?.length > 0 ? (
            (() => {
              const filteredPosts = socialPosts
                // Filter by platform if needed
                .filter((post) => {
                  const currentFilter = String(platformFilter || "All");
                  if (currentFilter === "All") return true;
                  const platform = post.data?.platform?.value || "instagram";
                  return platform.toLowerCase() === currentFilter.toLowerCase();
                })
                // Then filter by status if needed
                .filter((post) => {
                  const currentStatus = String(statusFilter || "All");
                  if (currentStatus === "All") return true;
                  const status = (post.data?.status?.value || "").toString().toLowerCase();
                  return status === currentStatus.toLowerCase();
                })
                // Then filter by timezone if needed
                .filter((post) => {
                  const currentTimezoneFilter = String(timezoneFilter || "All");
                  if (currentTimezoneFilter === "All") return true;
                  const postTimezone = post.data?.timezone?.value || "";
                  return String(postTimezone) === currentTimezoneFilter;
                })
                .sort((a, b) => {
                  const infoA = getPostDateInfo(a);
                  const infoB = getPostDateInfo(b);
                  const epochA = infoA?.epoch ?? Number.MAX_SAFE_INTEGER;
                  const epochB = infoB?.epoch ?? Number.MAX_SAFE_INTEGER;
                  return epochA - epochB;
                });

              const groupedPosts: Record<string, { display: string; posts: any[] }> = {};
              filteredPosts.forEach((post) => {
                const info = getPostDateInfo(post);
                const key = info?.isoKey || "no-date";
                const display = info?.displayDate || t("No Date");

                if (!groupedPosts[key]) groupedPosts[key] = { display, posts: [] };
                groupedPosts[key].posts.push(post);
              });

              return Object.entries(groupedPosts).map(([dateKey, groupValue]) => {
                const { display, posts } = groupValue;
                return (
                  <div key={dateKey}>
                    <div className="text-sm font-semibold leading-tight tracking-tight mb-3 px-1">
                      {display}
                    </div>
                    <div className="space-y-1">
                      {posts.map((post, index) => {
                        const data = post.data;
                        return (
                          <div
                            key={post.id || `${dateKey}-${index}`}
                            className="cursor-pointer transform transition-transform hover:scale-[1.02] active:scale-[0.98]"
                            onClick={() => onPostClick(post)}
                          >
                            <PostCard
                              title={
                                data?.summary?.value || data?.topic?.value || t("Untitled Post")
                              }
                              scheduledTimeUTC={data?.scheduledTimeUTC}
                              timezone={data?.timezone?.value}
                              status={data?.status?.value || t("Unscheduled")}
                              platform={data?.platform?.value || "instagram"}
                              postType={data?.postType?.value || "image"}
                              onDelete={onDeletePost ? () => onDeletePost(post.id) : undefined}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()
          ) : (
            <div className="h-full justify-items-center content-center pt-[240px]">
              <div>
                <img
                  src={`${getBaseUrl()}/static/images/empty-state.png`}
                  className="h-[83px] object-contain"
                  alt="Empty state"
                />
              </div>
              <div className="py-2 text-gray-600">{t("Let’s generate some content here")}</div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default PostsList;
